import { transformCollectionName, transformName } from '../helpers'
import { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import { APP_PATHS } from '__constants__'
import { getDocument } from 'services/api/firebase'
import pluralize from 'pluralize'
import { useGetCategories } from 'domains/Category/hooks'
import { useTranslations } from 'hooks'

const breadcrumbNames = Object.fromEntries(
  Object.entries(APP_PATHS).map((a) => a.reverse())
)
const getBreadcrumbItem = (name, url, isId, modelName, documentId) => {
  return {
    name,
    key: url,
    // If isId is true, replace the ':modelNameId' with the actual documentId in the url
    path: isId ? url.replace(`:${modelName}Id`, documentId) : url
  }
}
const useExtraBreadcrumbsItems = (collection) => {
  const [extraItems, setExtraItems] = useState(null)
  const [categories] = useGetCategories()

  const { t, language } = useTranslations()
  const location = useLocation()
  const params = useParams()
  useEffect(() => {
    const extractItems = async () => {
      const pathSnippets = location?.pathname.split('/').filter(Boolean)
      let collectionName
      if (
        pathSnippets?.includes('settings') &&
        !(pathSnippets[pathSnippets.length - 1] === 'settings')
      ) {
        collectionName = pathSnippets[1]?.includes('-')
          ? collection || transformCollectionName(pathSnippets[1])
          : collection || pathSnippets[1]
      } else {
        collectionName = collection || pathSnippets[0]
      }
      const modelName = pluralize.singular(
        collectionName?.includes('-')
          ? transformCollectionName(collectionName)
          : collectionName
      )

      const documentId = params?.[`${modelName}Id`]
      if (documentId && pathSnippets?.includes('settings')) {
        pathSnippets[2] = `:${modelName}Id`
      } else if (documentId) pathSnippets[1] = `:${modelName}Id`

      const breadcrumbItems = []
      for (let index = 0; index < pathSnippets?.length; index++) {
        const url = `/${pathSnippets?.slice(0, index + 1)?.join('/')}`
        const isId = pathSnippets[index].includes('Id')

        let transformedUrl
        if (
          pathSnippets[index] === 'edit' ||
          pathSnippets[index] === 'create'
        ) {
          transformedUrl = url
        } else {
          transformedUrl = breadcrumbNames[url] ? url : pluralize(url)
        }
        const transformedName = transformName(breadcrumbNames[transformedUrl])
        let name = t(transformedName)

        if (isId) {
          const document = await getDocument(collectionName, documentId)
          if (document) {
            name =
              document?.names?.[language.toUpperCase()] ||
              document?.name ||
              document?.title ||
              document?.orderNumber ||
              name
            // if document has a parentId, we are dealing with a subcategory,
            // so we add a breadcrumb for 'Products' and for the parent category

            if (document.parentId) {
              const parentCategory = categories?.find(
                (cat) => cat._id === document.parentId
              )
              //
              if (parentCategory) {
                breadcrumbItems.push(
                  getBreadcrumbItem(t('Products'), `/products`, false)
                )
                breadcrumbItems.push(
                  getBreadcrumbItem(
                    parentCategory?.names?.[language.toUpperCase()] ||
                      parentCategory.name,
                    `/categories/${parentCategory._id}/products`,
                    false
                  )
                )
              }
              // if the document is top level category, we add a 'Products' breadcrumb
            } else if (document.isTopLevel) {
              breadcrumbItems.push(
                getBreadcrumbItem(t('Products'), `/products`, false)
              )
            }
          }
        }
        // If the transformedName is not 'Categories' or 'Category', then add a breadcrumb with the current name and url.
        // This is done to avoid adding breadcrumbs for categories when we are not in a category path.
        if (
          !(transformedName === 'Categories' || transformedName === 'Category')
        ) {
          breadcrumbItems.push(
            getBreadcrumbItem(
              t(name),
              transformedUrl,
              isId,
              modelName,
              documentId
            )
          )
        }
      }

      setExtraItems(breadcrumbItems.filter(Boolean))
    }

    extractItems()
  }, [location?.pathname, params, t, categories, collection, language])
  return [extraItems]
}

export default useExtraBreadcrumbsItems
